import React from 'react';
import {
  IconBan,
  IconCalendarEvent,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleX,
  IconClock,
  IconDotsCircleHorizontal,
  IconEyeCheck,
  IconFileDescription,
  IconLayoutGrid,
  IconProgress,
  IconRefresh,
  IconSend,
  IconUsersGroup,
  IconUserShare,
  type Icon,
} from '@tabler/icons-react';
import { Group } from 'src/design/atoms/Group';
import { ScrollArea } from 'src/design/atoms/ScrollArea';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import classes from './WorkflowStatusPanel.module.css';

export interface IStatusSegment {
  value: string;
  label: string;
  count: number;
  color: string;
}

export interface IWorkflowStatusPanelProps {
  segments: IStatusSegment[];
  value: string;
  onChange: (value: string) => void;
  allValue?: string;
  allowDeselect?: boolean;
  ariaLabel?: string;
  layout?: 'wrap' | 'scroll';
}

const statusIcons: Record<string, Icon> = {
  all: IconLayoutGrid,
  draft: IconFileDescription,
  submitted: IconSend,
  'internal review': IconUsersGroup,
  reviewed: IconEyeCheck,
  're-reviewed': IconRefresh,
  pending: IconClock,
  'submitted to customer': IconUserShare,
  approved: IconCircleCheck,
  scheduled: IconCalendarEvent,
  'in progress': IconProgress,
  completed: IconCircleCheck,
  rejected: IconCircleX,
  voided: IconBan,
  other: IconDotsCircleHorizontal,
};

const iconFor = (label: string): Icon =>
  statusIcons[label.trim().toLowerCase()] ?? IconCircleDashed;

const segStyle = (color: string): React.CSSProperties =>
  ({
    '--seg-accent': `var(--mantine-color-${color}-6)`,
  } as React.CSSProperties);

/**
 * @registry Radiogroup of workflow status cards — each card pairs a colored status icon with the status label and a large live item count; replaces chip filters on workflow-driven job lists. Icons come from an internal map keyed by normalized status label. Feed it segments from useWorkflowStatusSegments (caller puts 'All' first). Clicking the active card falls back to allValue unless allowDeselect is false, which suits a filter whose source has no 'All' option.
 */
export const WorkflowStatusPanel: React.FC<IWorkflowStatusPanelProps> = ({
  segments,
  value,
  onChange,
  allValue = 'All',
  allowDeselect = true,
  ariaLabel = 'Filter by status',
  layout = 'wrap',
}) => {
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);

  const activate = (index: number) => {
    const seg = segments[index];
    if (!seg) return;
    if (seg.value === value && seg.value !== allValue) {
      if (!allowDeselect) return;
      onChange(allValue);
      return;
    }
    onChange(seg.value);
  };

  const move = (index: number) => {
    const seg = segments[index];
    if (!seg) return;
    onChange(seg.value);
    refs.current[index]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const len = segments.length;
    if (len === 0) return;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        move((index + 1) % len);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        move((index - 1 + len) % len);
        break;
      case 'Home':
        event.preventDefault();
        move(0);
        break;
      case 'End':
        event.preventDefault();
        move(len - 1);
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        activate(index);
        break;
      default:
        break;
    }
  };

  const hasActive = segments.some((seg) => seg.value === value);

  const blocks = segments.map((seg, index) => {
    const active = seg.value === value;
    const focusable = active || (!hasActive && index === 0);
    const StatusIcon = iconFor(seg.label);
    return (
      <UnstyledButton
        key={seg.value}
        ref={(el: HTMLButtonElement | null) => {
          refs.current[index] = el;
        }}
        role="radio"
        aria-checked={active}
        aria-label={`${seg.label}, ${seg.count} items`}
        tabIndex={focusable ? 0 : -1}
        data-active={active || undefined}
        data-empty={seg.count === 0 || undefined}
        className={classes.block}
        style={segStyle(seg.color)}
        onClick={() => activate(index)}
        onKeyDown={(event) => handleKeyDown(event, index)}
      >
        <Group wrap="nowrap" gap="md" align="center">
          <StatusIcon size={26} className={classes.icon} aria-hidden />
          <Stack gap={2}>
            <Text
              component="span"
              size="sm"
              c="bright"
              lineClamp={1}
              className={classes.label}
            >
              {seg.label}
            </Text>
            <Text
              component="span"
              size="xl"
              fw={700}
              c={seg.count === 0 ? 'dimmed' : 'bright'}
              className={classes.count}
            >
              {seg.count}
            </Text>
          </Stack>
        </Group>
      </UnstyledButton>
    );
  });

  const radiogroup = (
    <Group
      role="radiogroup"
      aria-label={ariaLabel}
      className={classes.radiogroup}
      gap="sm"
      wrap={layout === 'scroll' ? 'nowrap' : 'wrap'}
      mb="md"
    >
      {blocks}
    </Group>
  );

  if (layout === 'scroll') {
    return (
      <ScrollArea type="auto" scrollbarSize={8}>
        {radiogroup}
      </ScrollArea>
    );
  }

  return radiogroup;
};
